import { albumPrefixOf, pickAlbumSet } from '../collection/albumSet'
import {
  foilCount,
  ownedCount,
  type CopiesMap,
} from '../collection/owned'
import type { CardFoilId } from '../types/foil'

/** How a catalog card counts toward collection missions. */
export type CollectionCardKind = 'normal' | 'effect' | 'spell' | 'trap' | 'fusion'

export type CollectionFamily =
  | 'total'
  | 'set'
  | 'kind'
  | 'attribute'
  | 'race'
  | 'foil'
  | 'playset'

export const COLLECTION_FAMILIES = [
  'total',
  'set',
  'kind',
  'attribute',
  'race',
  'foil',
  'playset',
] as const satisfies readonly CollectionFamily[]

export type CollectionCardFact = {
  id: string
  set: string
  kind: CollectionCardKind
  attribute: string
  race: string
}

export type CollectionFactInput = {
  id: string | number
  type?: string
  attribute?: string
  race?: string
  card_sets?: unknown
}

/** One step on a track. `all` means every card in that track's pool. */
export type CollectionRung = {
  target: number | 'all'
  coins: number
  packs?: number
}

export type CollectionTrack = {
  id: string
  family: CollectionFamily
  key: string
  rungs: readonly CollectionRung[]
}

export type CollectionRungRow = {
  id: string
  trackId: string
  family: CollectionFamily
  key: string
  index: number
  progress: number
  target: number
  /** This step is the whole pool, not a fixed number. */
  whole: boolean
  coins: number
  packs: number
  done: boolean
  claimed: boolean
}

export type CollectionTrackView = {
  trackId: string
  family: CollectionFamily
  key: string
  rungs: CollectionRungRow[]
  /** Next unclaimed step, or the last step once the track is finished. */
  focus: CollectionRungRow
  pending: number
  finished: boolean
}

/** Paid collection milestones. Part of GET /account/state. */
export type CollectionClaimResult = {
  /** Steps this request paid. Empty when they were already claimed. */
  claimed: string[]
  coins: number
  packs?: Record<string, number>
  /** Every milestone this account has been paid, including this request. */
  known?: string[]
}

const PLAYSET_COPIES = 3

/** Foils that count as "this rarity or better". */
const FOIL_AT_LEAST: Record<string, readonly CardFoilId[]> = {
  rare: ['rare', 'super', 'ultra', 'ghost', 'helios', 'nightglass', 'stormwake'],
  super: ['super', 'ultra', 'ghost', 'helios', 'nightglass', 'stormwake'],
  ultra: ['ultra', 'ghost', 'helios', 'nightglass', 'stormwake'],
  special: ['ghost', 'helios', 'nightglass', 'stormwake'],
}

function rung(
  target: number | 'all',
  coins: number,
  packs?: number,
): CollectionRung {
  return packs ? { target, coins, packs } : { target, coins }
}

function track(
  id: string,
  family: CollectionFamily,
  key: string,
  rungs: readonly CollectionRung[],
): CollectionTrack {
  return { id, family, key, rungs }
}

const BIG_SET = [
  rung(25, 2),
  rung(50, 3),
  rung(100, 5),
  rung('all', 10, 1),
] as const

const SMALL_SET = [rung(10, 2), rung(20, 3), rung('all', 5)] as const

const WIDE_ATTR = [rung(15, 2), rung(40, 3), rung('all', 6)] as const
const MID_ATTR = [rung(8, 2), rung('all', 4)] as const

const BIG_RACE = [rung(5, 1), rung(15, 2), rung('all', 5)] as const
const MID_RACE = [rung(5, 1), rung('all', 3)] as const
const SMALL_RACE = [rung('all', 2)] as const

/**
 * Permanent collection tracks. A numeric step at or above the live pool
 * is dropped when progress is built, so `all` stays the last real step.
 */
export const COLLECTION_TRACKS: readonly CollectionTrack[] = [
  track('total', 'total', 'all', [
    rung(30, 2),
    rung(60, 3),
    rung(100, 5),
    rung(150, 8),
    rung(200, 10, 1),
    rung(250, 15),
    rung(300, 20),
    rung('all', 25, 3),
  ]),
  track('set-LOB', 'set', 'LOB', BIG_SET),
  track('set-SDY', 'set', 'SDY', SMALL_SET),
  track('set-SDK', 'set', 'SDK', SMALL_SET),
  track('set-MRD', 'set', 'MRD', BIG_SET),
  track('kind-normal', 'kind', 'normal', [
    rung(25, 2),
    rung(75, 4),
    rung('all', 8),
  ]),
  track('kind-effect', 'kind', 'effect', [
    rung(15, 2),
    rung(40, 4),
    rung('all', 8),
  ]),
  track('kind-spell', 'kind', 'spell', [
    rung(15, 2),
    rung(40, 4),
    rung('all', 8),
  ]),
  track('kind-trap', 'kind', 'trap', [
    rung(5, 2),
    rung(10, 3),
    rung('all', 5),
  ]),
  track('kind-fusion', 'kind', 'fusion', [
    rung(3, 2),
    rung(10, 3),
    rung('all', 6),
  ]),
  track('attr-DARK', 'attribute', 'DARK', WIDE_ATTR),
  track('attr-EARTH', 'attribute', 'EARTH', WIDE_ATTR),
  track('attr-LIGHT', 'attribute', 'LIGHT', MID_ATTR),
  track('attr-WATER', 'attribute', 'WATER', MID_ATTR),
  track('attr-WIND', 'attribute', 'WIND', MID_ATTR),
  track('attr-FIRE', 'attribute', 'FIRE', [rung(5, 1), rung('all', 3)]),
  track('race-Spellcaster', 'race', 'Spellcaster', BIG_RACE),
  track('race-Warrior', 'race', 'Warrior', BIG_RACE),
  track('race-Fiend', 'race', 'Fiend', BIG_RACE),
  track('race-Beast', 'race', 'Beast', MID_RACE),
  track('race-Dragon', 'race', 'Dragon', MID_RACE),
  track('race-Insect', 'race', 'Insect', MID_RACE),
  track('race-Machine', 'race', 'Machine', MID_RACE),
  track('race-Aqua', 'race', 'Aqua', MID_RACE),
  track('race-Zombie', 'race', 'Zombie', MID_RACE),
  track('race-Winged Beast', 'race', 'Winged Beast', MID_RACE),
  track('race-Beast-Warrior', 'race', 'Beast-Warrior', MID_RACE),
  track('race-Fish', 'race', 'Fish', MID_RACE),
  track('race-Thunder', 'race', 'Thunder', MID_RACE),
  track('race-Fairy', 'race', 'Fairy', SMALL_RACE),
  track('race-Plant', 'race', 'Plant', SMALL_RACE),
  track('race-Rock', 'race', 'Rock', SMALL_RACE),
  track('race-Pyro', 'race', 'Pyro', SMALL_RACE),
  track('race-Dinosaur', 'race', 'Dinosaur', SMALL_RACE),
  track('race-Reptile', 'race', 'Reptile', SMALL_RACE),
  track('foil-rare', 'foil', 'rare', [
    rung(10, 2),
    rung(30, 3),
    rung(75, 5),
  ]),
  track('foil-super', 'foil', 'super', [rung(5, 2), rung(20, 4)]),
  track('foil-ultra', 'foil', 'ultra', [rung(3, 3), rung(10, 5)]),
  track('foil-special', 'foil', 'special', [rung(1, 3), rung(3, 5)]),
  track('playset', 'playset', '3', [
    rung(10, 2),
    rung(50, 4),
    rung(150, 8),
  ]),
]

const MILESTONE_IDS = new Set<string>()
for (const def of COLLECTION_TRACKS) {
  def.rungs.forEach((_, index) => {
    MILESTONE_IDS.add(`${def.id}:${index}`)
  })
}

export function collectionMilestoneId(trackId: string, index: number): string {
  return `${trackId}:${index}`
}

export function isCollectionMilestoneId(value: unknown): value is string {
  return typeof value === 'string' && MILESTONE_IDS.has(value)
}

export function collectionKindOf(type: string | undefined): CollectionCardKind {
  const text = type ?? ''
  if (text.includes('Spell')) return 'spell'
  if (text.includes('Trap')) return 'trap'
  if (text.includes('Fusion')) return 'fusion'
  if (text.includes('Normal')) return 'normal'
  return 'effect'
}

function setsOf(raw: unknown): readonly { set_code?: string }[] {
  if (!Array.isArray(raw)) return []
  const sets: { set_code?: string }[] = []
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue
    const code = (item as { set_code?: unknown }).set_code
    if (typeof code === 'string') sets.push({ set_code: code })
    else sets.push({})
  }
  return sets
}

function blank(value: string | undefined): string {
  if (!value || value === '-') return ''
  return value
}

/**
 * One fact per catalog card. Spells and traps keep an empty attribute.
 */
export function collectionFactsFromSources(
  cards: readonly CollectionFactInput[],
): CollectionCardFact[] {
  return cards.map((card) => {
    const code = pickAlbumSet(setsOf(card.card_sets))?.set_code ?? ''
    return {
      id: String(card.id),
      set: albumPrefixOf(code),
      kind: collectionKindOf(card.type),
      attribute: blank(card.attribute),
      race: blank(card.race),
    }
  })
}

function inPool(def: CollectionTrack, fact: CollectionCardFact): boolean {
  switch (def.family) {
    case 'total':
    case 'foil':
    case 'playset':
      return true
    case 'set':
      return fact.set === def.key
    case 'kind':
      return fact.kind === def.key
    case 'attribute':
      return fact.attribute === def.key
    case 'race':
      return fact.race === def.key
    default:
      return false
  }
}

function seenFoil(
  copies: CopiesMap,
  album: CopiesMap | undefined,
  cardId: string,
  foil: CardFoilId,
): boolean {
  if (foilCount(copies, cardId, foil) > 0) return true
  return album ? foilCount(album, cardId, foil) > 0 : false
}

function seenCard(
  copies: CopiesMap,
  album: CopiesMap | undefined,
  cardId: string,
): boolean {
  if (ownedCount(copies, cardId) > 0) return true
  return album ? ownedCount(album, cardId) > 0 : false
}

function countsToward(
  def: CollectionTrack,
  copies: CopiesMap,
  fact: CollectionCardFact,
  album?: CopiesMap,
): boolean {
  if (def.family === 'playset') {
    return ownedCount(copies, fact.id) >= PLAYSET_COPIES
  }
  if (def.family === 'foil') {
    const foils = FOIL_AT_LEAST[def.key]
    if (!foils) return false
    return foils.some((foil) => seenFoil(copies, album, fact.id, foil))
  }
  return seenCard(copies, album, fact.id)
}

/**
 * Live steps for the cards this account owns. Numeric steps that sit at
 * or above the pool are omitted. Claimed ids that are not in the result
 * were already paid and are not offered again.
 */
export function collectionProgress(
  copies: CopiesMap,
  facts: readonly CollectionCardFact[],
  claimed: readonly string[],
  album?: CopiesMap,
): CollectionRungRow[] {
  const paid = new Set(claimed)
  const rows: CollectionRungRow[] = []
  for (const def of COLLECTION_TRACKS) {
    const poolFacts = facts.filter((fact) => inPool(def, fact))
    if (poolFacts.length === 0) continue
    let progress = 0
    for (const fact of poolFacts) {
      if (countsToward(def, copies, fact, album)) progress += 1
    }
    def.rungs.forEach((step, index) => {
      const whole = step.target === 'all'
      const target = step.target === 'all' ? poolFacts.length : step.target
      if (target < 1 || (step.target !== 'all' && target >= poolFacts.length)) return
      const id = collectionMilestoneId(def.id, index)
      rows.push({
        id,
        trackId: def.id,
        family: def.family,
        key: def.key,
        index,
        progress,
        target,
        whole,
        coins: step.coins,
        packs: step.packs ?? 0,
        done: progress >= target,
        claimed: paid.has(id),
      })
    })
  }
  return rows
}

export function collectionPendingCount(rows: readonly CollectionRungRow[]): number {
  let n = 0
  for (const row of rows) {
    if (row.done && !row.claimed) n += 1
  }
  return n
}

/** One card per track, in catalog order. Tracks with no live steps are skipped. */
export function collectionTrackViews(
  rows: readonly CollectionRungRow[],
): CollectionTrackView[] {
  const byTrack = new Map<string, CollectionRungRow[]>()
  for (const row of rows) {
    const list = byTrack.get(row.trackId)
    if (list) list.push(row)
    else byTrack.set(row.trackId, [row])
  }
  const views: CollectionTrackView[] = []
  for (const def of COLLECTION_TRACKS) {
    const rungs = byTrack.get(def.id)
    if (!rungs || rungs.length === 0) continue
    const focus = rungs.find((row) => !row.claimed) ?? rungs[rungs.length - 1]!
    let pending = 0
    let finished = true
    for (const row of rungs) {
      if (!row.claimed) finished = false
      if (row.done && !row.claimed) pending += 1
    }
    views.push({
      trackId: def.id,
      family: def.family,
      key: def.key,
      rungs,
      focus,
      pending,
      finished,
    })
  }
  return views
}
